import { Suspense, lazy } from "react";
import Header from "./components/Header";
import Footer from "./components/Footer";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import "./App.css";
import "./styles/bootstrap-custom.scss";
import Home from './components/home/Home';
import { AuthProvider, useAuth } from "./services/authContext";
import { FeatureConfigProvider, useFeatureConfig } from "./services/featureConfigContext";
import Seo from './components/Seo';
import PsychologistUsersPage from './components/Psychologist/PsychologistUsersPage';
import PsychologistUserProfilePage from './components/Psychologist/PsychologistUserProfilePage';
import PsychologistUserResultDetailPage from './components/Psychologist/PsychologistUserResultDetailPage';
import AccessControlPage from './components/Profile/AccessControlPage';

const YandexMetrika = lazy(() => import("./components/YandexMetrika"));
const Login = lazy(() => import("./components/Auth/Login"));
const Register = lazy(() => import("./components/Auth/Register"));
const TestList = lazy(() => import("./components/Tests/TestList"));
const TestDetail = lazy(() => import("./components/Tests/TestDetail"));
const TestEditor = lazy(() => import("./components/Tests/TestEditor"));
const ResultsList = lazy(() => import("./components/Results/ResultsList"));
const ResultDetail = lazy(() => import("./components/Results/ResultDetail"));
const Profile = lazy(() => import("./components/Profile/Profile"));
const AdminDashboard = lazy(() => import("./components/Admin/AdminDashboard"));
const SubscriptionPage = lazy(() => import("./components/Subscription/SubscriptionPage"));
const SubscriptionSuccess = lazy(() => import("./components/Subscription/SubscriptionSuccess"));
const SubscriptionFail = lazy(() => import("./components/Subscription/SubscriptionFail"));
const ForgotPassword = lazy(() => import("./components/Auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./components/Auth/ResetPassword"));
const Contacts = lazy(() => import("./components/Contacts/Contacts"));
const UserMessagesPage = lazy(() => import("./components/Messages/UserMessagesPage"));

const LOGIN_REQUIRED_MESSAGE = 'Войдите, чтобы продолжить.';
const ADMIN_REQUIRED_MESSAGE = 'У вас нет доступа к этой странице. Только администраторы могут создавать и редактировать тесты.';
const PSYCHOLOGIST_REQUIRED_MESSAGE = 'У вас нет доступа к этой странице. Только психологи могут просматривать пользователей.';
const USER_REQUIRED_MESSAGE = 'У вас нет доступа к этой странице.';

function LoadingScreen() {
  return (
    <div className="container mt-4 text-center">
      Загрузка...
    </div>
  );
}

function PrivateRoute() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location, message: LOGIN_REQUIRED_MESSAGE }} />;
  }

  return <Outlet />;
}

function AdminRoute() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location, message: LOGIN_REQUIRED_MESSAGE }} />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/tests" replace state={{ message: ADMIN_REQUIRED_MESSAGE }} />;
  }

  return <Outlet />;
}

function PsychologistRoute() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location, message: LOGIN_REQUIRED_MESSAGE }} />;
  }

  if (user.role !== 'psychologist') {
    return <Navigate to="/tests" replace state={{ message: PSYCHOLOGIST_REQUIRED_MESSAGE }} />;
  }

  return <Outlet />;
}

function UserRoute() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location, message: LOGIN_REQUIRED_MESSAGE }} />;
  }

  if (user.role !== 'user') {
    return <Navigate to="/tests" replace state={{ message: USER_REQUIRED_MESSAGE }} />;
  }

  return <Outlet />;
}

function PublicOnlyRoute() {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

function PaymentsEnabledRoute() {
  const { features, loading } = useFeatureConfig();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!features.payments) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return <Outlet />;
}

function EmailEnabledRoute() {
  const { features, loading } = useFeatureConfig();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!features.email) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <Outlet />;
}

function MessagingEnabledRoute() {
  const { features, loading } = useFeatureConfig();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!features.messaging) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }

  return <Outlet />;
}

function RegistrationEnabledRoute() {
  const { features, loading } = useFeatureConfig();
  const location = useLocation();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!features.registration) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <Outlet />;
}

function AppRoutes() {
  const { features } = useFeatureConfig();

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/tests" element={<TestList />} />
      <Route path="/contacts" element={<Contacts />} />
      <Route element={<EmailEnabledRoute />}>
        <Route path="/forgot-password" element={<ForgotPassword />} />
      </Route>
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route element={<PublicOnlyRoute />}>
        <Route path="/login" element={<Login />} />
        <Route element={<RegistrationEnabledRoute />}>
          <Route path="/register" element={<Register />} />
        </Route>
      </Route>
      <Route element={<PrivateRoute />}>
        <Route element={<AdminRoute />}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/tests" element={<Navigate to="/admin?tab=tests" replace />} />
          <Route path="/tests/new" element={<TestEditor />} />
          <Route path="/tests/:id/edit" element={<TestEditor />} />
        </Route>
        {features.payments && (
          <Route element={<PaymentsEnabledRoute />}>
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/subscription/success" element={<SubscriptionSuccess />} />
            <Route path="/subscription/fail" element={<SubscriptionFail />} />
          </Route>
        )}
        <Route path="/tests/:id" element={<TestDetail />} />
        <Route path="/results" element={<ResultsList />} />
        <Route path="/results/:id" element={<ResultDetail />} />
        {features.analytics && (
          <Route path="/analytics" element={<Navigate to="/results?tab=analytics" replace />} />
        )}
        <Route path="/profile" element={<Profile />} />
        <Route element={<UserRoute />}>
          {features.psychologist && (
            <Route path="/profile/access" element={<AccessControlPage />} />
          )}
          <Route element={<MessagingEnabledRoute />}>
            <Route path="/messages" element={<UserMessagesPage />} />
          </Route>
        </Route>
        {features.psychologist && (
          <Route element={<PsychologistRoute />}>
            <Route path="/psychologist/users" element={<PsychologistUsersPage />} />
            <Route
              path="/psychologist/users/:userId/results/:resultId"
              element={<PsychologistUserResultDetailPage />}
            />
            <Route path="/psychologist/users/:userId" element={<PsychologistUserProfilePage />} />
          </Route>
        )}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <FeatureConfigProvider>
      <AuthProvider>
        <div className="app-container">
          <Router>
            <Seo />
            <Suspense fallback={null}>
              <YandexMetrika />
            </Suspense>
            <Header />

            <main className="app-main">
              <Suspense fallback={<LoadingScreen />}>
                <AppRoutes />
              </Suspense>
            </main>

            <Footer />
          </Router>
        </div>
      </AuthProvider>
    </FeatureConfigProvider>
  );
}

export default App;
